import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';
import { store } from './store/store';

// Mobile users of this dashboard are traders who prioritise information
// density over comfort — they want the whole table on one screen. We keep
// the desktop minimum at 0.7rem but let mobile go smaller (see the
// per-cell sx in Triggers.js / Trades.js). This constant is exported for
// any future code that wants a readable desktop floor.
const MIN_SMALL_FONT = '0.7rem';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
  // Custom tokens used across the app; read via theme.custom.* or copied
  // into explicit values in sx when needed.
  custom: {
    minSmallFont: MIN_SMALL_FONT,
  },
  components: {
    // Keyboard-focus ring (A6). Invisible on mouse/touch; shows when the
    // user actually tabs to an element so keyboard navigation is usable.
    MuiCssBaseline: {
      styleOverrides: {
        ':focus-visible': {
          outline: '2px solid #90caf9',
          outlineOffset: '2px',
          borderRadius: '2px',
        },
        // Remove default mouse-click focus outline that creates noise
        // while preserving the keyboard-only one above.
        ':focus:not(:focus-visible)': {
          outline: 'none',
        },
      },
    },
    // Small IconButton — mobile-first tight padding. An earlier version
    // forced 6px padding globally which made mobile tables too loose.
    // 2px keeps the tap target reachable while staying compact in a
    // high-density view; desktop pages that want more room can override
    // with sx at the call site.
    MuiIconButton: {
      styleOverrides: {
        sizeSmall: {
          padding: 2,
        },
      },
    },
    // Row separator + subtle hover for dense tables (A4). No data
    // behavior change — purely a visual affordance to track rows.
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.03)',
          },
          '&:not(:last-child) > td': {
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        // Keep inline sx overrides working; this just ensures no
        // default bottom-border is drawn since TableRow handles it.
        root: {
          borderBottom: 'none',
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter>
        <ThemeProvider theme={darkTheme}>
          <CssBaseline />
          <App />
        </ThemeProvider>
      </BrowserRouter>
    </Provider>
  </React.StrictMode>
);
